import { useState, type CSSProperties } from 'react';

// G1-1c — SILENT-LOSS pre-read panel. Both import portals (craft + evaluate) call /api/parse and can get back
// three kinds of exception the user should see BEFORE the CV is treated as fully read: (a) a contact_candidate
// carrying a field the CURRENT profile doesn't have yet (never overwritten — fill-missing-only, confirmed by the
// user's own checkbox click); (b) source_coverage chunks — text the segmenter could not attribute to any
// candidate (so a whole section can't silently vanish behind dropped:0); (c) duplicate_suspects — a semantic
// near-duplicate the segmenter deliberately did NOT auto-merge. When there are NO exceptions this panel is never
// rendered at all — the existing fast paths (evaluate auto-adopt; craft's confirm screen) are unchanged.

export interface ContactCandidate { name: string; phone: string; city: string }
export interface CoverageChunk { start: number; end: number; first_line: string }
export interface DuplicateSuspect { a_index: number; b_index: number; label: string }
export interface MissingContactFields { name: boolean; phone: boolean; city: boolean }

const FIELD_LABEL: Record<keyof ContactCandidate, string> = { name: 'Name', phone: 'Phone', city: 'City' };

export function ImportExceptions({
  contactCandidate, missingFields, sourceText, chunks, duplicateSuspects, onConfirmContact, onContinue,
}: {
  contactCandidate: ContactCandidate | null;
  missingFields: MissingContactFields;
  sourceText: string;
  chunks: CoverageChunk[];
  duplicateSuspects: DuplicateSuspect[];
  // fill-missing-only: called with ONLY the fields the user checked — never the ones the profile already has.
  onConfirmContact: (fields: Partial<ContactCandidate>) => void;
  onContinue: () => void;
}) {
  const offeredFields = (['name', 'phone', 'city'] as const).filter((f) => !!contactCandidate?.[f] && missingFields[f]);
  const [checked, setChecked] = useState<Record<string, boolean>>(() => Object.fromEntries(offeredFields.map((f) => [f, true])));
  const [openChunk, setOpenChunk] = useState<number | null>(null);

  function handleContinue() {
    if (offeredFields.length > 0) {
      const toSend: Partial<ContactCandidate> = {};
      for (const f of offeredFields) if (checked[f]) toSend[f] = contactCandidate![f];
      if (Object.keys(toSend).length > 0) onConfirmContact(toSend);
    }
    onContinue();
  }

  return (
    <div style={S.panel} data-testid="import-exceptions">
      <h2 style={S.h2}>Before we continue</h2>

      {offeredFields.length > 0 && (
        <div style={S.block}>
          <p style={S.p}>We found contact details in your CV that aren’t on your profile yet:</p>
          {offeredFields.map((f) => (
            <label key={f} style={S.checkLbl} data-testid={`import-exceptions-contact-${f}`}>
              <input
                type="checkbox"
                checked={!!checked[f]}
                onChange={(e) => setChecked((c) => ({ ...c, [f]: e.target.checked }))}
                data-testid={`import-exceptions-contact-${f}-checkbox`}
              />
              <span><strong>{FIELD_LABEL[f]}:</strong> {contactCandidate![f]} — <em>Use this on your CV</em></span>
            </label>
          ))}
        </div>
      )}

      {chunks.length > 0 && (
        <div style={S.block}>
          <p style={S.heading}>Not carried into the conventional CaRMS CV:</p>
          {chunks.map((c, i) => (
            <div key={i} style={S.chunk} data-testid={`import-exceptions-chunk-${i}`}>
              <button
                type="button"
                className="btn btn-ghost"
                style={S.chunkToggle}
                onClick={() => setOpenChunk(openChunk === i ? null : i)}
                data-testid={`import-exceptions-chunk-toggle-${i}`}
              >
                {openChunk === i ? '▾' : '▸'} {c.first_line || '(untitled text)'}
              </button>
              {openChunk === i && (
                <pre style={S.pre} data-testid={`import-exceptions-chunk-body-${i}`}>{sourceText.slice(c.start, c.end)}</pre>
              )}
            </div>
          ))}
        </div>
      )}

      {duplicateSuspects.length > 0 && (
        <div style={S.block}>
          {duplicateSuspects.map((d, i) => (
            <p key={i} style={S.dup} data-testid={`import-exceptions-dup-${i}`}>
              This may appear twice: {d.label} — both were imported; remove one in Review if so.
            </p>
          ))}
        </div>
      )}

      <button className="btn btn-primary" onClick={handleContinue} data-testid="import-exceptions-continue">Continue</button>
    </div>
  );
}

const S: Record<string, CSSProperties> = {
  panel: { maxWidth: 760, margin: '24px auto', padding: 'var(--space-4)', border: '1px solid var(--rule)', borderRadius: 10, background: 'var(--card)' },
  h2: { margin: '0 0 var(--space-2)', fontSize: 'var(--fs-h2)' },
  block: { margin: 'var(--space-3) 0' },
  p: { margin: 'var(--space-1) 0', fontSize: 'var(--fs-body)' },
  heading: { margin: 'var(--space-1) 0', fontSize: 'var(--fs-body)', fontWeight: 600 },
  checkLbl: { display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', fontSize: 'var(--fs-body)', margin: 'var(--space-2) 0' },
  chunk: { margin: 'var(--space-2) 0' },
  chunkToggle: { fontSize: 'var(--fs-small)', textAlign: 'left', padding: '2px 0' },
  pre: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 200, overflow: 'auto', background: 'var(--surface-subtle)', border: '1px solid var(--rule)', borderRadius: 6, padding: 'var(--space-3)', fontSize: 'var(--fs-label)', fontFamily: 'inherit', marginTop: 'var(--space-1)' },
  dup: { margin: 'var(--space-1) 0', fontSize: 'var(--fs-body)', color: 'var(--ink-subtle)' },
};
